export interface User {
  id: number;
  name: string;
  email: string;
  role: "admin" | "user";
  is_active?: boolean;
  preferences?: {
    balance?: number;
    currency?: string;
    [key: string]: any;
  };
  balance?: number;
  created_at: string;
  updated_at: string;
}

export interface HistoricalMetric {
  id: number;
  user_id: number;
  date: string;
  revenue: number;
  page_views: number;
  clicks: number;
  impressions: number;
  cpc: number;
  cpm: number;
  ctr: number;
  created_at?: string;
  updated_at?: string;
}

export interface RevenueSource {
  id: number;
  user_id: number;
  date: string;
  source_type: "search" | "display" | "video" | "mobile" | "native";
  amount: number;
  created_at?: string;
  updated_at?: string;
}

export interface DashboardSummary {
  total_revenue: number;
  total_page_views: number;
  total_clicks: number;
  total_impressions: number;
  avg_cpc: number;
  avg_cpm: number;
  avg_ctr: number;
  balance?: number;
  currency?: string;
}

export interface DateRange {
  start: string;
  end: string;
}

export interface MetricsState {
  summary: DashboardSummary | null;
  dailyMetrics: HistoricalMetric[];
  revenueBySource: RevenueSource[];
  adUnits: AdUnit[];
  historicalData: HistoricalMetric[];
  realTimeMetrics: any;
  isLoading: boolean;
  period: "7d" | "30d" | "90d" | "custom";
  dateRange: DateRange;
}

export interface AdUnit {
  id: number;
  user_id: number;
  name: string;
  ad_type: string;
  size: string;
  description?: string;
  ad_code?: any;
  settings?: any;
  revenue: number;
  impressions: number;
  clicks: number;
  ctr: number;
  status: string;
  is_archived: boolean;
  created_at: string;
  updated_at: string;
}

export interface AdminConfiguration {
  id: number;
  config_key: string;
  config_value: any;
  description: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

export interface AdminState {
  configurations: AdminConfiguration[];
  users: User[];
  selectedUser: User | null;
  userMetrics: any;
  notifications: Notification[]; // Changed from any[] to Notification[]
  notificationStatistics: any | null; // Add this property
  isLoading: boolean;
  error: string | null;
}

export interface ApiResponse<T = any> {
  data: T;
  message?: string;
  status: number;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData extends LoginCredentials {
  name: string;
  password_confirmation: string;
}

export interface Report {
  id: number;
  user_id: number;
  name: string;
  type: string;
  format: string;
  filters?: any;
  data?: any;
  file_path?: string;
  file_size?: string;
  status: string;
  generated_at?: string;
  created_at: string;
  updated_at: string;
}

export interface UserMetricsResponse {
  user: User;
  summary: {
    total_revenue: number;
    total_page_views: number;
    total_clicks: number;
    ad_units_count: number;
    metrics_count?: number;
  };
  historical_metrics: HistoricalMetric[];
  revenue_sources: RevenueSource[];
  ad_units: AdUnit[];
}

// types.ts - Add these interfaces

export interface Notification {
  id: number;
  title: string;
  message: string;
  type:
    | "system"
    | "revenue"
    | "performance"
    | "payment"
    | "maintenance"
    | "alert";
  priority: "low" | "medium" | "high" | "critical";
  metadata?: any;
  expires_at?: string;
  is_global: boolean;
  is_active: boolean;
  sent_at: string;
  read_count?: number;
  total_sent?: number;
  dismissed_count?: number;
  created_at?: string;
  updated_at?: string;
  user_ids?: number[];
}

export interface UserNotification {
  id: number;
  user_id: number;
  notification_id: number;
  read: boolean;
  dismissed: boolean;
  read_at?: string;
  created_at: string;
  updated_at: string;
  notification?: Notification;
  user?: User;
}

export interface NotificationStatistics {
  summary: {
    total: number;
    active: number;
    global: number;
    total_read: number;
    total_unread: number;
  };
  by_type: Array<{ type: string; count: number }>;
  recent: Notification[];
}

// Helper function to get user balance from either direct field or preferences
export const getUserBalance = (user: User): number => {
  return user.balance || user.preferences?.balance || 0;
};

// Helper function to check if user is active
export const isUserActive = (user: User): boolean => {
  return user.is_active !== false;
};
